import { type ReactNode, useState } from 'react'
import styled from 'styled-components'
import { Carousel } from '@/features/shop/components/Carousel'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { InView } from '@/features/shared/components/ScrollIntoView'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { screenSizes } from '@shipt/design-system-themes'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { type RichTextElement } from '@/features/cms/components/CMS/types'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { EAGER_LOAD_SHELF_INDEX } from '@/features/shared/constants/global'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'

/**
 * A "View all" CTA. `viewAllHref` does the navigating; `onViewAll` is for tracking only.
 * Controls that don't navigate (e.g. scroll-to-section) should stay plain buttons.
 */
export type ShelfViewAll = {
  onViewAll: () => void | Promise<unknown>
  viewAllHref: string
}

/**
 * All-or-nothing: `{}` means no View all. Build it with `getShelfViewAll` or a typed return
 * and spread it; an inline `cond ? {...} : {}` won't compile because TS merges it into optional fields.
 */
export type ShelfViewAllProps = ShelfViewAll | { onViewAll?: never; viewAllHref?: never }

export type Props = Readonly<
  {
    slideSpacing?: number
    title?: string
    hideTitle?: boolean
    titleSmall?: boolean
    children: ReactNode
    shelfIndex?: number
    minHeight?: number
    onSlideChange?: () => void
    className?: string
    hasFlag?: boolean
    subTitle?: string | RichTextElement[]
    store_id?: number
  } & ShelfViewAllProps
>

export function Shelf({
  slideSpacing,
  title = '',
  hideTitle = false,
  titleSmall = false,
  onViewAll,
  viewAllHref,
  children = null,
  shelfIndex = 0,
  minHeight = 0,
  onSlideChange,
  className,
  hasFlag = false,
  subTitle,
  store_id,
}: Props) {
  const { screenSm } = useMediaQuery()
  const [isCarouselVisible, setIsCarouselVisible] = useState(shelfIndex <= EAGER_LOAD_SHELF_INDEX)
  const { trackCMSElementViewed } = useCMSTrackingEvents()

  const handleInView = () => {
    setIsCarouselVisible(true)

    trackCMSElementViewed({
      shelf_index: shelfIndex || -1,
      shelf_name: title,
      store_id,
    })
  }

  const renderSubtitle = () => {
    if (typeof subTitle === 'string') {
      return <Body variant="secondary">{subTitle}</Body>
    }
    if (subTitle) {
      return <Subheading>{subTitle.map(renderElement)}</Subheading>
    }
  }

  return (
    <ShelfWrapper className={className}>
      <InView onInView={handleInView}>
        <Row
          align="center"
          justify="space-between"
          marginBottom={!hideTitle && !titleSmall ? 'lg' : undefined}
        >
          {!hideTitle && (
            <Column>
              <Row align="center">
                <SkeletonConsumer width={130} height={24}>
                  <Headline size={titleSmall ? 'sm' : 'lg'} as="h2">
                    {title}
                  </Headline>
                </SkeletonConsumer>
              </Row>
              {renderSubtitle()}
            </Column>
          )}
          {!!onViewAll && !!viewAllHref && (
            <SkeletonConsumer width={95} height={24}>
              <TextButtonLink
                href={viewAllHref}
                onClick={onViewAll}
                aria-label={`View all ${title}`}
              >
                View all
              </TextButtonLink>
            </SkeletonConsumer>
          )}
        </Row>
      </InView>
      <ShelfCarouselWrapper minHeight={minHeight}>
        {!!isCarouselVisible && (
          <ShelfCarousel
            title={title}
            freeMode
            spaceBetween={slideSpacing}
            simulateTouch={screenSm}
            shelfIndex={shelfIndex}
            onSlideChange={onSlideChange}
            minHeight={minHeight}
            hasFlag={hasFlag}
            store_id={store_id}
          >
            {children}
          </ShelfCarousel>
        )}
      </ShelfCarouselWrapper>
    </ShelfWrapper>
  )
}

const ShelfWrapper = styled(Column)`
  position: relative;
  margin-bottom: 1rem;

  @media ${screenSizes.tablet} {
    margin-bottom: 1.5rem;
  }
`

const ShelfCarouselWrapper = styled.div<{ minHeight: number }>`
  position: relative;
  min-height: ${({ minHeight }) => minHeight}px;
`

const ShelfCarousel = styled(Carousel)<{ minHeight: number; hasFlag: boolean }>`
  padding: ${({ hasFlag }) =>
    hasFlag ? '0.625rem 1.625rem 0.25rem 0.4rem' : '0.25rem 1.625rem 0.25rem 0.3125rem'};
  margin: 0;
  min-height: ${({ minHeight }) => minHeight}px;
`
const Subheading = styled.div`
  *,
  & {
    color: ${({ theme }) => theme.color.content.base.neutral.secondary};
  }
`
